// theme-sources/js/core-ui/components/ziggy-drawer.js
import { sectionLogger } from "./_logger";

const DEBUG = true;
const logger = sectionLogger("ZiggyDrawer", DEBUG);

class ZiggyDrawer extends HTMLElement {
  static instances = new Map();

  connectedCallback() {
    // logger.info("connected", this);

    this.name = this.getAttribute("name");
    this.side = this.getAttribute("side") || "right";

    if (!this.name) {
      logger.warn("missing name", this);
      return;
    }

    // ⚠️ garde-fou : name dupliqué
    if (ZiggyDrawer.instances.has(this.name)) {
      logger.warn("duplicate drawer name", {
        name: this.name,
        existing: ZiggyDrawer.instances.get(this.name),
        current: this,
      });
    }
    // ✅ enregistrement officiel
    ZiggyDrawer.instances.set(this.name, this);

    this.overlay = this.querySelector("[data-drawer-overlay]");
    this.onKeydown = this.onKeydown.bind(this);

    this.overlay?.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.close();
    });

    /* ================================
     * Delegation interne pour data-close
     * (robuste aux re-render sections)
     * ================================ */
    this.addEventListener("click", (event) => {
      const closeBtn = event.target.closest("[data-close]");
      if (!closeBtn) return;
      event.preventDefault();
      this.close();
    });

    document.addEventListener("keydown", this.onKeydown);
  }

  disconnectedCallback() {
    ZiggyDrawer.instances.delete(this.name);
    document.removeEventListener("keydown", this.onKeydown);
  }

  /* ======================================================
   * Public API
   * ====================================================== */

  open() {
    if (this.isOpen) return;

    this.previousFocus = document.activeElement;
    this.setAttribute("aria-hidden", "false");

    // Force reflow to ensure animation triggers
    this.offsetHeight;

    requestAnimationFrame(() => {
      this.classList.add("is-open");
      document.body.classList.add("overflow-hidden");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const focusables = this.getFocusableElements();
        focusables[0]?.focus();
        document.addEventListener("keydown", this.onTabTrap);
      },
      { once: true },
    );
  }

  close() {
    if (!this.isOpen) return;

    this.classList.remove("is-open");
    document.body.classList.remove("overflow-hidden");

    document.removeEventListener("keydown", this.onTabTrap);

    this.previousFocus?.focus();

    this.setAttribute("aria-hidden", "true");
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  onTabTrap = (event) => {
    if (event.key !== "Tab") return;

    const focusables = this.getFocusableElements();
    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    const isInsideDrawer = this.contains(document.activeElement);

    if (!isInsideDrawer) {
      event.preventDefault();
      first.focus();
      return;
    }

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
      return;
    }

    if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  get isOpen() {
    return this.classList.contains("is-open");
  }

  getFocusableElements() {
    return this.querySelectorAll(
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
    );
  }

  /* ======================================================
   * Internals
   * ====================================================== */

  onKeydown(event) {
    if (event.key !== "Escape") return;
    if (!this.isOpen) return;

    this.close();
  }

  /* ======================================================
   * Static helpers
   * ====================================================== */

  static open(name) {
    ZiggyDrawer.instances.get(name)?.open();
  }

  static close(name) {
    ZiggyDrawer.instances.get(name)?.close();
  }

  static toggle(name) {
    ZiggyDrawer.instances.get(name)?.toggle();
  }
}

if (!customElements.get("ziggy-drawer")) {
  customElements.define("ziggy-drawer", ZiggyDrawer);
}
